import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Utils from '../../../Utils';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Table from '../../../Components/Table/Table';

export default function T25v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t25Tests, setT25Tests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(true);

    const [selectedRows, setSelectedRows] = useState(t25Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;
        setTestsLoading(true);
        try {
            const { data } = await API.getTest25TestsByUserId(currentUser._id);
            if (data) {
                setT25Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setTestsLoading(false);
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            volume25: 25,
            volume400: 400,
            time25: '00:00,000',
            time400: '00:00,000',
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest25Test(currentUser._id, {
                time25Meters,
                time400Meters,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });

            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });

            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = /^([0-5]?\d):([0-5]\d),(\d{1,3})$/.exec(durationStr);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time25Meters = currentInput.time25 ? parseDuration(currentInput.time25) : 0;
    const time400Meters = currentInput.time400 ? parseDuration(currentInput.time400) : 0;

    const velocity25 = currentInput.time25 ? 25 / time25Meters : 0;
    const velocity400 = currentInput.time400 ? 400 / time400Meters : 0;

    const calculateCSS = time400Meters => (time400Meters ? (400 / time400Meters) * 0.92 : 0);
    const calculateMSS = time25Meters => (time25Meters ? 25 / time25Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time25m',
            Header: () => <div className={styles.center}>Time 25m (sec)</div>,
            accessor: 'time25m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time25Meters}</div>
        },
        {
            id: 'time400m',
            Header: () => <div className={styles.center}>Time 400m (sec)</div>,
            accessor: 'time400m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time400Meters}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div className={styles.center}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div className={styles.center}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div className={styles.center}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div className={styles.center}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div className={styles.center}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div className={styles.center}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        volume25: currentInput.volume25,
        time25Meters: time25Meters,
        velocity25Meters: velocity25,
        volume400: currentInput.volume400,
        time400Meters: time400Meters,
        velocity400Meters: velocity400
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time25Meters > 0 && time400Meters > 0 ? [currentTestData] : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time25Meters, y: 25 },
                { x: row.time400Meters, y: 400 }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time25Meters > 0 && row.time400Meters > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity25Meters, y: 25 },
                    { x: row.velocity400Meters, y: 400 }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPage} data-tests-page>
            <header className={styles.pageHead}>
                <div className={styles.pageHeadText}>
                    <h2 className={styles.pageTitle}>25M-400M test</h2>
                    <p className={styles.pageSub}>A 25 m and a 400 m time trial · intermediate or advanced swimmers · one or two days</p>
                </div>
                <div className={styles.difficulty}>
                    <span className={styles.difficultyLabel} aria-hidden='true'>
                        Difficulty
                    </span>
                    <ProgressBar className={styles.difficultyBar}>
                        <ProgressBar variant='success' now={30} aria-label='Test difficulty' />
                    </ProgressBar>
                </div>
            </header>
            <div className={styles.entryGrid}>
                <section className={`${styles.panel} ${styles.areaForm}`} aria-labelledby='t25-data-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t25-data-title' className={styles.panelTitle}>
                                Test data
                            </h3>
                            <p className={styles.panelSub}>
                                Enter your times as mm:ss,ms. Velocity is calculated as you type.
                            </p>
                        </div>
                    </div>
                    <form className={`${styles.panelBody} ${styles.entryForm}`}>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>25 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t25-volume25' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t25-volume25'
                                disabled={true}
                                {...register('volume25', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t25-time25' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t25-time25'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time25', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time25 ? true : undefined}
                                aria-describedby={errors.time25 ? 't25-time25-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time25 && (
                                <span id='t25-time25-error' className={styles.fieldError}>
                                    {errors.time25.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t25-velocity25' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t25-velocity25'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume25,
                                    currentInput.time25
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>400 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t25-volume400' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t25-volume400'
                                disabled={true}
                                {...register('volume400', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t25-time400' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t25-time400'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time400', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss' format is required"
                                    }
                                })}
                                aria-invalid={errors.time400 ? true : undefined}
                                aria-describedby={errors.time400 ? 't25-time400-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time400 && (
                                <span id='t25-time400-error' className={styles.fieldError}>
                                    {errors.time400.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t25-velocity400' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t25-velocity400'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.volume400,
                                    currentInput.time400
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={`${styles.fieldGroup} ${styles.detailsGroup}`}>
                        <legend className={styles.fieldGroupLegend}>Session details</legend>
                        <div className={styles.field}>
                            <label htmlFor='t25-poolSize' className={styles.fieldLabel}>
                                Pool size (m) <span className={styles.fieldHint}>· for reference only</span>
                            </label>
                            <select
                                id='t25-poolSize'
                                className={`${styles.fieldInput} form-control`}
                                {...register('poolSize')}
                            >
                                <option value={''}>-</option>
                                <option value={25}>25m</option>
                                <option value={50}>50m</option>
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t25-testDate' className={styles.fieldLabel}>
                                Test date & time <span className={styles.fieldHint}>· defaults to now</span>
                            </label>
                            <input
                                id='t25-testDate'
                                type='datetime-local'
                                {...register('testDate')}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <div className={styles.formActions}>
                        <button
                            className={`${styles.submitButton} btn btn-primary`}
                            onClick={handleSubmit(onSubmit)}
                        >
                            Submit
                        </button>
                    </div>
                    </form>
                </section>
                <section className={`${styles.panel} ${styles.areaSummary}`} aria-labelledby='t25-summary-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t25-summary-title' className={styles.panelTitle}>
                                Summary
                            </h3>
                            <p className={styles.panelSub}>This entry next to the latest saved values.</p>
                        </div>
                    </div>
                    <table className={styles.summaryTable}>
                        <thead>
                            <tr>
                                <th scope='col' className={styles.metricName}>
                                    Metric
                                </th>
                                <th scope='col'>Current test</th>
                                <th scope='col'>
                                    Latest for {currentUser ? currentUser.firstName : ''}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerCss}`} />
                                    Critical Swim Speed (CSS)
                                </th>
                                <td className={styles.metricValue}>
                                    {calculateCSS(time400Meters).toFixed(3)}
                                </td>
                                <td className={styles.metricValue}>{currentUser?.currentCSS?.toFixed(3) || 0}</td>
                            </tr>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerMss}`} />
                                    Maximum Swim Speed (MSS)
                                </th>
                                <td className={styles.metricValue}>{calculateMSS(time25Meters).toFixed(3) || 0}</td>
                                <td className={styles.metricValue}>{currentUser?.currentMSS?.toFixed(3) || 0}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className={styles.summaryUnit}>Velocities in m/sec.</p>
                </section>
                <section
                    className={`${styles.panel} ${styles.areaHistory}`}
                    aria-labelledby='t25-history-title'
                    data-tests-history
                >
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t25-history-title' className={styles.panelTitle}>
                                Previous tests
                            </h3>
                            <p className={styles.panelSub}>
                                Select a row to show or hide it on the charts below. Your current entry is added once its
                                times are valid.
                            </p>
                        </div>
                    </div>
                    <div className={styles.panelFlush}>
                        <Table
                            onRowClick={handleRowClick}
                            columns={columns}
                            data={t25Tests}
                            isLoading={testsLoading}
                            rowsPerPage={7}
                            updatePageSize={false}
                            pageSkipping={false}
                            selected={selectedRows}
                            showSelectionIndicator={true}
                            tableLayout='auto'
                        />
                    </div>
                </section>
                <section className={`${styles.panel} ${styles.areaGuide}`} aria-labelledby='t25-guide-title'>
                    <div className={styles.panelHead}>
                        <h3 id='t25-guide-title' className={styles.panelTitle}>
                            How to run this test
                        </h3>
                    </div>
                    <div className={`${styles.panelBody} ${styles.guide}`}>
                    <p>
                        This test is aimed for <b>advanced</b> or <b>intermediate</b> swimmers.
                        It takes <b>one</b> or <b>two</b> <b>days</b> to complete this test.
                    </p>
                    <p>
                        If you complete the test in <b>one</b> day, you must complete the{' '}
                        <b>25 m</b> test <b>first</b> and then, after at{' '}
                        <b>least twenty minutes</b>, the <b>400 m </b> test.
                    </p>
                    <p>
                        If we complete the test in <b>two days</b>, we should complete one test
                        in one day and the second test in the next day.
                    </p>
                    <p>The two trials will start in the pool without a start.</p>
                    </div>
                </section>
            </div>
            <div className={styles.chartGrid}>
                <section className={styles.panel} aria-labelledby='t25-chart-time-title'>
                    <h3 id='t25-chart-time-title' className={styles.srOnly}>
                        Distance - Time
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Time'}
                            titleY={'Distance (m)'}
                            titleX={'Time (sec)'}
                            datasets={datasetsDistanceTime}
                            xAxisType='linear'
                        />
                    </div>
                </section>
                <section className={styles.panel} aria-labelledby='t25-chart-velocity-title'>
                    <h3 id='t25-chart-velocity-title' className={styles.srOnly}>
                        Distance - Velocity
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Velocity'}
                            titleY={'Distance (m)'}
                            titleX={'Velocity (m/sec)'}
                            datasets={datasetsDistanceVelocity}
                            xAxisType='linear'
                        />
                    </div>
                </section>
            </div>
            <p className={styles.footNote}>
                For your Training Zones Limits check your{' '}
                <Link to='../../dashboard' className={styles.footLink}>
                    Dashboard
                </Link>
                .
            </p>
        </div>
    );
}
